import { Link } from "react-router-dom";
import { TrendingUp, Mail, MapPin, AlertTriangle } from "lucide-react";

const Footer = () => {
  return (
    <footer className="bg-navy-700 text-white py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold font-heading">Compoundr.</span>
            </Link>
            <p className="text-slate-300 text-sm leading-relaxed">
              Helping Canadian students start their investing journey with confidence. No hype, just education.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: "Home", path: "/" },
                { name: "Our Mission", path: "/mission" },
                { name: "Blog", path: "/blog" },
                { name: "Free Seminars", path: "/seminars" },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-300 hover:text-emerald-400 transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Resources</h4>
            <ul className="space-y-3">
              {[
                { name: "All Tools", path: "/tools" },
                { name: "Products", path: "/courses" },
                { name: "Privacy Policy", path: "/privacy" },
                { name: "Terms of Service", path: "/terms" },
                { name: "*", path: "/admin" },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-300 hover:text-emerald-400 transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-bold text-lg mb-4">Get in Touch</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-slate-300 text-sm">
                <Mail className="w-4 h-4 text-emerald-400" />
                compoundrca@gmail.com
              </li>
              <li className="flex items-center gap-2 text-slate-300 text-sm">
                <MapPin className="w-4 h-4 text-emerald-400" />
                Toronto, Canada
              </li>
            </ul>
          </div>
        </div>

        {/* Legal Disclaimer */}
        <div className="border-t border-slate-600 mt-12 pt-8">
          <div className="bg-slate-800/50 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-slate-400 leading-relaxed space-y-2">
                <p className="font-semibold text-slate-300">Important Legal Disclaimer</p>
                <p>
                  <strong>Compoundr is an independent educational platform.</strong> We are NOT financial advisors, 
                  licensed investment professionals, or registered with any securities regulatory authority. We are NOT 
                  affiliated with, endorsed by, or connected to the Government of Canada, any provincial government, 
                  the Canada Revenue Agency (CRA), or any financial regulatory body.
                </p>
                <p>
                  All content provided on this website is for <strong>general educational and informational purposes only</strong> and 
                  should not be construed as professional financial, investment, tax, or legal advice. The information 
                  presented does not take into account your specific financial situation, goals, or risk tolerance.
                </p>
                <p>
                  <strong>Investing involves risk, including the potential loss of principal.</strong> Past performance 
                  does not guarantee future results. Before making any investment decisions, you should consult with a 
                  qualified, licensed financial advisor or professional who can provide personalized advice based on 
                  your individual circumstances.
                </p>
                <p>
                  Any references to specific investment products, platforms, or strategies are for educational purposes 
                  only and do not constitute recommendations or endorsements. We do not guarantee any specific outcomes 
                  or returns from following the information provided on this website.
                </p>
                <p>
                  By using this website, you acknowledge that you have read and understood this disclaimer and agree 
                  that Compoundr and its creators shall not be held liable for any decisions you make based on the 
                  information provided herein.
                </p>
              </div>
            </div>
          </div>

          <p className="text-slate-400 text-sm text-center">
            © {new Date().getFullYear()} Compoundr. All rights reserved. Built for Canadian students by a Canadian student.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
